/**
 * KAN-874: общий список "Требуют внимания" мобильной доски по всем этапам.
 * Доска с ?attention=1 (общий кеш useCrmPipeline, второго запроса нет) плюс
 * хвосты этапов, которых в ответе доски нет целиком (свёрнутый этап, больше
 * 50 клиентов, "Без этапа"): порции ?stage=&attention=1 до конца, одним
 * запросом. Ключ под CRM_PIPELINE_QUERY_KEY: обновляется вместе с доской и
 * блоком внимания (ATTENTION_QUERY_KEY).
 * KAN-880: фильтры доски (search / manager) у доски и хвостов.
 * KAN-882: доска вида board (managers / funnel); хвосты ?stage= без исхода (open).
 */
import type { CrmRange } from '@/lib/crmTable';
import { boardActivityParams } from '@/lib/crmBoardActivity';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCrmPipeline } from '@/hooks/useCrmPipeline';
import { attentionListItems, attentionTailStages, type AttentionListItem } from '@/lib/crmAttentionList';
import { crmApi } from '@/services/api/crm';
import { CRM_PIPELINE_QUERY_KEY } from '@/services/api/queryKeys';
import type { CrmBoardView, CrmLeadFilterParams, CrmPipelineCard, CrmStageFilter } from '@/types/crmPipeline';

const NO_FILTERS: CrmLeadFilterParams = {};

/** Порция хвоста: максимум, который принимает backend (limit 1..200). */
const TAIL_PAGE_SIZE = 200;

export const crmAttentionTailsKey = (
  activity: CrmRange | null,
  stages: readonly CrmStageFilter[],
  filters: CrmLeadFilterParams = NO_FILTERS
) => [...CRM_PIPELINE_QUERY_KEY, 'attention-tails', activity, stages.map(String), filters] as const;

async function fetchStageTail(
  stage: CrmStageFilter,
  activity: CrmRange | null,
  filters: CrmLeadFilterParams
): Promise<CrmPipelineCard[]> {
  const cards: CrmPipelineCard[] = [];
  let offset: number | null = 0;
  while (offset !== null) {
    const page = await crmApi.getPipelineStage({
      ...boardActivityParams(activity),
      attention: '1',
      ...filters,
      stage,
      offset,
      limit: TAIL_PAGE_SIZE,
    });
    cards.push(...page.cards);
    offset = page.next_offset;
  }
  return cards;
}

export interface CrmAttentionList {
  items: AttentionListItem[];
  isLoading: boolean;
  error: unknown;
}

export function useCrmAttentionList(
  board: CrmBoardView,
  activity: CrmRange | null,
  filters: CrmLeadFilterParams = NO_FILTERS
): CrmAttentionList {
  const pipeline = useCrmPipeline(board, activity, true, filters);
  const stages = useMemo(() => (pipeline.data ? attentionTailStages(pipeline.data) : []), [pipeline.data]);
  const tails = useQuery({
    queryKey: crmAttentionTailsKey(activity, stages, filters),
    queryFn: async () =>
      (await Promise.all(stages.map((stage) => fetchStageTail(stage, activity, filters)))).flat(),
    enabled: stages.length > 0,
    retry: false,
  });
  const items = useMemo(
    () => (pipeline.data ? attentionListItems(pipeline.data, tails.data ?? []) : []),
    [pipeline.data, tails.data]
  );
  return {
    items,
    isLoading: pipeline.isLoading || (stages.length > 0 && tails.isLoading),
    error: pipeline.error ?? tails.error,
  };
}
